import * as React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { refreshSession } from '@/lib/api';
import { Mark } from '@/components/layout/mark';
import { useSession } from './session';

/**
 * The redirect target after Google.
 *
 * The callback itself is a browser redirect handled by the server; by the time
 * we land here the refresh cookie is already set. All that remains is to
 * exchange it for an access token, which is one call to `POST /auth/refresh`.
 *
 * A failed exchange goes back to the sign-in page carrying the reason, so the
 * expert gets a specific sentence rather than a spinner that eventually gives
 * up on them.
 */
export function AuthCallbackPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { reload } = useSession();
  const ran = React.useRef(false);
  const [slow, setSlow] = React.useState(false);

  React.useEffect(() => {
    // React mounts effects twice in development; a second exchange would consume
    // the rotated refresh token and revoke the whole family.
    if (ran.current) return;
    ran.current = true;

    if (params.get('auth') === 'failed') {
      const reason = params.get('reason') ?? 'exchange_failed';
      navigate(`/login?auth=failed&reason=${encodeURIComponent(reason)}`, { replace: true });
      return;
    }

    const slowTimer = setTimeout(() => setSlow(true), 4000);

    void (async () => {
      const ok = await refreshSession();
      clearTimeout(slowTimer);
      if (!ok) {
        navigate('/login?auth=failed&reason=exchange_failed', { replace: true });
        return;
      }
      try {
        await reload();
        navigate('/queue', { replace: true });
      } catch {
        navigate('/login?auth=failed&reason=role_mismatch', { replace: true });
      }
    })();

    return () => clearTimeout(slowTimer);
  }, [params, navigate, reload]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-ground px-6">
      <div className="text-center">
        <Mark className="mx-auto h-6 w-6 text-ink" />
        <p className="mt-4 text-sm text-ink-2">Signing you in…</p>
        {slow ? <p className="mt-1 font-mono text-tiny text-ink-3">Still waiting on the token exchange.</p> : null}
      </div>
    </div>
  );
}
